import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import EmailModeSettings from '../../components/EmailModeSettings';
import PushToggleButton from '../../components/PushToggleButton';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Settings, Lock, Eye, EyeOff, Bell, BellOff, Mail, Trash2, AlertTriangle, BellRing, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const { notificationsEnabled, toggleNotifications, isSupported, permissionGranted, requestPermission } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Delete history state
  const [deleteHistoryDialog, setDeleteHistoryDialog] = useState(false);
  const [deletingHistory, setDeletingHistory] = useState(false);

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success('Push notifications enabled! You will receive alerts when staff clock in/out.');
    } else {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  const handleDeleteAllHistory = async () => {
    setDeletingHistory(true);
    try {
      const response = await axios.delete(`${API}/admin/clock-events/all`);
      toast.success(response.data.message);
      setDeleteHistoryDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete history');
    } finally {
      setDeletingHistory(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail || !emailPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/change-email`, {
        new_email: newEmail,
        current_password: emailPassword
      });
      
      toast.success('Email updated successfully! Please login again with your new email.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully! Please login again.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        {/* Header */}
        <div className="border-b border-border pb-6">
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Settings className="w-6 h-6 text-primary" strokeWidth={1.5} />
            SETTINGS
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your account settings
          </p>
        </div>

        {/* Email Notification Mode — controls Automatic / Manual / Disabled */}
        <EmailModeSettings />

        {/* Web Push Notifications — added 2026-02 */}
        <div className="max-w-md">
          <PushToggleButton />
        </div>

        {/* Change Password */}
        <Card className="bg-card border-border rounded-sm max-w-md">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" strokeWidth={1.5} />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Current Password
                </Label>
                <div className="relative">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="bg-background border-border rounded-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  New Password
                </Label>
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="bg-background border-border rounded-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Confirm New Password
                </Label>
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-background border-border rounded-sm"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Email */}
        <Card className="bg-card border-border rounded-sm max-w-md">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" strokeWidth={1.5} />
              Change Email
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleChangeEmail} className="space-y-4">
              <div className="p-3 bg-accent/50 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  Current email: <span className="text-foreground font-medium">{user?.email}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  New Email Address
                </Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="your.company@email.com"
                  required
                  data-testid="new-email-input"
                  className="bg-background border-border rounded-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Current Password (to confirm)
                </Label>
                <Input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  required
                  data-testid="email-password-input"
                  className="bg-background border-border rounded-sm"
                />
              </div>
              
              <Button
                type="submit"
                disabled={emailLoading}
                data-testid="change-email-btn"
                className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
              >
                {emailLoading ? 'Updating...' : 'Update Email'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notifications Settings */}
        <Card className="bg-card border-border rounded-sm max-w-md">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              {notificationsEnabled ? (
                <Bell className="w-4 h-4 text-primary" strokeWidth={1.5} />
              ) : (
                <BellOff className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              )}
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  Push Notifications
                </Label>
                <p className="text-xs text-muted-foreground">
                  Receive alerts when employees clock in/out
                </p>
              </div>
              <Switch
                data-testid="notifications-toggle"
                checked={notificationsEnabled}
                onCheckedChange={(checked) => {
                  toggleNotifications(checked);
                  toast.success(checked ? 'Notifications enabled' : 'Notifications disabled');
                }}
                disabled={!isSupported}
              />
            </div>
            {!isSupported && (
              <p className="text-xs text-orange-400">
                Your browser does not support push notifications
              </p>
            )}
            {isSupported && notificationsEnabled && !permissionGranted && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEnableNotifications}
                data-testid="enable-push-alerts-btn"
                className="w-full justify-start gap-2 border-primary/50 hover:bg-primary/10 text-primary rounded-sm"
              >
                <BellRing className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Enable Push Alerts</span>
              </Button>
            )}
            {isSupported && notificationsEnabled && permissionGranted && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-900/50 rounded-sm">
                <BellRing className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-xs text-green-600 dark:text-green-400 uppercase tracking-wider">
                  Push Alerts Active
                </span>
              </div>
            )}
            {!notificationsEnabled && (
              <div className="p-3 bg-accent/50 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  All notifications are currently disabled. You won't receive any alerts until you enable this.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="bg-card border-border rounded-sm max-w-md">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              {theme === 'dark' ? (
                <Moon className="w-4 h-4 text-primary" strokeWidth={1.5} />
              ) : (
                <Sun className="w-4 h-4 text-primary" strokeWidth={1.5} />
              )}
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Dark Mode</Label>
                <p className="text-xs text-muted-foreground">
                  Switch between light and dark theme
                </p>
              </div>
              <Switch
                data-testid="dark-mode-toggle"
                checked={theme === 'dark'}
                onCheckedChange={() => {
                  toggleTheme();
                  toast.success(theme === 'dark' ? 'Light mode enabled' : 'Dark mode enabled');
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-card border-border rounded-sm max-w-md">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase">
              Account Info
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Name</p>
              <p className="text-sm">{user?.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">Email</p>
              <p className="text-sm">{user?.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">Role</p>
              <p className="text-sm capitalize">{user?.role}</p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone - Delete History */}
        <Card className="bg-card border-red-900/50 rounded-sm max-w-md">
          <CardHeader className="border-b border-red-900/50 pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-red-400">
                Delete All Clock History
              </Label>
              <p className="text-xs text-muted-foreground">
                This will permanently delete ALL clock in/out records. This action cannot be undone.
              </p>
            </div>
            <Button
              onClick={() => setDeleteHistoryDialog(true)}
              data-testid="delete-all-history-btn"
              className="w-full bg-red-600 hover:bg-red-500 text-foreground font-bold"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              DELETE ALL CLOCK HISTORY
            </Button>
          </CardContent>
        </Card>

        {/* Delete History Confirmation Dialog */}
        <AlertDialog open={deleteHistoryDialog} onOpenChange={setDeleteHistoryDialog}>
          <AlertDialogContent className="bg-card border-border rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider uppercase text-red-500 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete All Clock History
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete ALL clock in/out records? This action is permanent and cannot be undone. All employees will be marked as "off duty".
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllHistory}
                disabled={deletingHistory}
                data-testid="confirm-delete-history-btn"
                className="bg-red-600 text-foreground hover:bg-red-500 rounded-sm"
              >
                {deletingHistory ? 'Deleting...' : 'Yes, Delete All'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
